import { useEffect, useRef, useState } from 'react';

const PLAYLIST_ID = 'PLGtFlgZq9Dt8';
const PLAYER_ID = 'mukwi-youtube-player';

const STORAGE_KEYS = {
  index: 'mukwi_bgm_index',
  time: 'mukwi_bgm_time',
  volume: 'mukwi_bgm_volume',
  playing: 'mukwi_bgm_playing',
};

const readNumber = (key, fallback = 0) => {
  const value = Number(localStorage.getItem(key));

  return Number.isFinite(value) ? value : fallback;
};

const BgmPlayer = () => {
  const playerRef = useRef(null);
  const saveTimerRef = useRef(null);

  const [ready, setReady] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [title, setTitle] = useState('BGM 재생목록');
  const [volume, setVolume] = useState(
    () => readNumber(STORAGE_KEYS.volume, 50),
  );

  const updateTitle = player => {
    const data = player?.getVideoData?.();

    if (data?.title) {
      setTitle(data.title);
    }
  };

  const savePlaybackState = () => {
    const player = playerRef.current;

    if (!player?.getCurrentTime) {
      return;
    }

    const playlistIndex = player.getPlaylistIndex?.();
    const currentTime = player.getCurrentTime?.();
    const playerState = player.getPlayerState?.();

    if (Number.isFinite(playlistIndex) && playlistIndex >= 0) {
      localStorage.setItem(STORAGE_KEYS.index, String(playlistIndex));
    }

    if (Number.isFinite(currentTime) && currentTime >= 0) {
      localStorage.setItem(STORAGE_KEYS.time, String(currentTime));
    }

    localStorage.setItem(
      STORAGE_KEYS.playing,
      String(playerState === window.YT?.PlayerState?.PLAYING),
    );
  };

  useEffect(() => {
    const createPlayer = () => {
      if (playerRef.current || !window.YT?.Player) {
        return;
      }

      const savedIndex = Math.max(
        0,
        Math.floor(readNumber(STORAGE_KEYS.index, 0)),
      );

      const savedTime = Math.max(
        0,
        readNumber(STORAGE_KEYS.time, 0),
      );

      playerRef.current = new window.YT.Player(PLAYER_ID, {
        width: 260,
        height: 200,

        playerVars: {
          listType: 'playlist',
          list: PLAYLIST_ID,
          index: savedIndex,
          start: Math.floor(savedTime),
          playsinline: 1,
          rel: 0,
        },

        events: {
          onReady: event => {
            const player = event.target;

            player.setVolume(volume);

            /*
             * 브라우저 자동재생 제한 때문에 자동으로 소리를 재생하지 않고,
             * 저장된 곡과 위치까지만 준비합니다.
             */
            player.cuePlaylist({
              listType: 'playlist',
              list: PLAYLIST_ID,
              index: savedIndex,
              startSeconds: savedTime,
            });

            setReady(true);
            updateTitle(player);
          },

          onStateChange: event => {
            const player = event.target;
            const isPlaying =
              event.data === window.YT.PlayerState.PLAYING;

            setPlaying(isPlaying);
            updateTitle(player);
            savePlaybackState();
          },
        },
      });

      saveTimerRef.current = window.setInterval(
        savePlaybackState,
        3000,
      );
    };

    if (window.YT?.Player) {
      createPlayer();
    } else {
      if (
        !document.querySelector(
          'script[src="https://www.youtube.com/iframe_api"]',
        )
      ) {
        const script = document.createElement('script');

        script.src = 'https://www.youtube.com/iframe_api';
        document.head.appendChild(script);
      }

      const previousCallback = window.onYouTubeIframeAPIReady;

      window.onYouTubeIframeAPIReady = () => {
        previousCallback?.();
        createPlayer();
      };
    }

    const handlePageHide = () => {
      savePlaybackState();
    };

    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('beforeunload', handlePageHide);

    return () => {
      savePlaybackState();

      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('beforeunload', handlePageHide);

      if (saveTimerRef.current) {
        window.clearInterval(saveTimerRef.current);
      }

      playerRef.current?.destroy?.();
      playerRef.current = null;
    };
  }, []);

  const togglePlayback = () => {
    const player = playerRef.current;

    if (!ready || !player) {
      return;
    }

    if (playing) {
      player.pauseVideo();
    } else {
      player.playVideo();
    }
  };

  const playPrevious = () => {
    playerRef.current?.previousVideo();
  };

  const playNext = () => {
    playerRef.current?.nextVideo();
  };

  const changeVolume = event => {
    const nextVolume = Number(event.target.value);

    setVolume(nextVolume);
    localStorage.setItem(STORAGE_KEYS.volume, String(nextVolume));
    playerRef.current?.setVolume(nextVolume);
  };

  return (
    <aside className='mukwi-bgm-player' aria-label='BGM 플레이어'>
      <div className='mukwi-bgm-player__video'>
        <div id={PLAYER_ID} />
      </div>

      <div className='mukwi-bgm-player__info'>
        <i className='fa fa-music' aria-hidden='true' />
        <span title={title}>{title}</span>
      </div>

      <div className='mukwi-bgm-player__controls'>
        <button
          type='button'
          onClick={playPrevious}
          disabled={!ready}
          aria-label='이전 곡'
          title='이전 곡'
        >
          <i className='fa fa-step-backward' aria-hidden='true' />
        </button>

        <button
          type='button'
          onClick={togglePlayback}
          disabled={!ready}
          aria-label={playing ? '일시정지' : '재생'}
          title={playing ? '일시정지' : '재생'}
        >
          <i
            className={`fa ${playing ? 'fa-pause' : 'fa-play'}`}
            aria-hidden='true'
          />
        </button>

        <button
          type='button'
          onClick={playNext}
          disabled={!ready}
          aria-label='다음 곡'
          title='다음 곡'
        >
          <i className='fa fa-step-forward' aria-hidden='true' />
        </button>

        <i
          className='fa fa-volume-up mukwi-bgm-player__volume-icon'
          aria-hidden='true'
        />

        <input
          type='range'
          min='0'
          max='100'
          value={volume}
          onChange={changeVolume}
          aria-label='BGM 볼륨'
        />
      </div>
    </aside>
  );
};

export default BgmPlayer;
